import React from 'react';
import PropTypes from 'prop-types';
import VerticalTabsItem from '../VerticalTabsItem';
import VerticalTabsContext from './VerticalTabsContext';
import {
  TabsGroupProps,
  VerticalTabsProps,
  VerticalTabsFooterProps,
} from './VerticalTabs.types';
import { SIZE, SKIN } from './VerticalTabs.constants';
import VerticalTabsIconItem from '../VerticalTabsIconItem';
import { useTabNavigation } from '../common/useTabNavigation';

/** Vertical tabs navigation panel. */
const Footer = ({ children }: VerticalTabsFooterProps) => (
  <VerticalTabsItem type="action">{children}</VerticalTabsItem>
);

const TabsGroup = ({ title = '', children }: TabsGroupProps) => (
  <>
    <VerticalTabsItem type="title">{title}</VerticalTabsItem>
    {children}
  </>
);

TabsGroup.propTypes = {
  title: PropTypes.string,
};

const VerticalTabs = <TabId extends string | number = number>({
  dataHook,
  children,
  size = SIZE.MEDIUM,
  skin = SKIN.LIGHT,
  activeTabId,
  onChange,
}: VerticalTabsProps<TabId>) => {
  const tabNavigationRef = useTabNavigation({ orientation: 'vertical' });

  return (
    <VerticalTabsContext.Provider
      // @ts-expect-error - didn't manage to make context work with components generics
      value={{ size, skin, activeTabId, onChange }}
    >
      <div
        data-hook={dataHook}
        aria-orientation="vertical"
        role="tablist"
        data-active-tab-id={activeTabId}
        ref={tabNavigationRef}
      >
        {children}
      </div>
    </VerticalTabsContext.Provider>
  );
};

VerticalTabs.TabsGroup = TabsGroup;
VerticalTabs.TabItem = VerticalTabsItem;
VerticalTabs.IconItem = VerticalTabsIconItem;
VerticalTabs.Footer = Footer;
VerticalTabs.displayName = 'VerticalTabs';
VerticalTabs.propTypes = {
  skin: PropTypes.oneOf([SKIN.LIGHT, SKIN.NEUTRAL, SKIN.STANDARD]),
  size: PropTypes.oneOf([SIZE.TINY, SIZE.SMALL, SIZE.MEDIUM]),
  activeTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  children: PropTypes.arrayOf(PropTypes.node),
  dataHook: PropTypes.string,
};

export default VerticalTabs;
